import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Container, Row, Col, Form, Button } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

dayjs.extend(customParseFormat);

const _coordSanitize = (val) =>
  String(val ?? '')
    .replace(/[^0-9.\-]/g, '')
    .replace(/(?!^)-/g, '')
    .replace(/(\..*)\./g, '$1');

const _fmt6 = (v) => (v === '' || v == null ? '' : Number.parseFloat(v).toFixed(6));
const _clamp = (num, min, max) => Math.min(Math.max(num, min), max);
const isAlnumSpace = (s) => /^[A-Za-z0-9 ]+$/.test(s);

// Define toHHMM first since it's used in toMinutes
const toHHMM = (raw) => {
  if (!raw) return null;
  const cleaned = raw.replace('.', ':');
  const m = /^(\d{1,2}):?(\d{2})$/.exec(
    cleaned.includes(':') ? cleaned : cleaned.replace(/\D/g, '')
  );
  if (!m) return null;
  let [, h, mm] = m;
  h = h.padStart(2, '0');
  mm = mm.padStart(2, '0');
  if (+h > 23 || +mm > 59) return null;
  return `${h}:${mm}`;
};

const toMinutes = (timeValue) => {
  if (!timeValue) return null;
  
  let timeStr;
  if (dayjs.isDayjs(timeValue)) {
    timeStr = timeValue.format('HH:mm');
  } else {
    timeStr = timeValue;
  }
  
  const hhmm = toHHMM(timeStr);
  if (!hhmm) return null;
  
  const [hours, minutes] = hhmm.split(':').map(Number);
  return hours * 60 + minutes;
};

const handleStopNameKeyDown = (e) => {
  const k = e.key;
  const ctrlCmd = e.ctrlKey || e.metaKey;
  if (/^[A-Za-z0-9]$/.test(k)) return;
  if (k === ' ' && e.currentTarget.selectionStart > 0) return;
  const nav = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'];
  if (nav.includes(k)) return;
  if (ctrlCmd && ['a', 'c', 'v', 'x', 'z', 'y'].includes(k.toLowerCase())) return;
  e.preventDefault();
};

const handleStopNamePaste = (e) => {
  const t = (e.clipboardData || window.clipboardData).getData('text');
  if (!isAlnumSpace(t)) {
    e.preventDefault();
    toast.error('Only letters, numbers and spaces are allowed in Stop Name.');
  }
};

const AddBusRoutes = () => {
  const userData = sessionStorage.getItem('user');
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const routeLocation = useLocation();
  const navigate = useNavigate();

  const [editId, setEditId] = useState(null);
  const [routelocations, setRoutelocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stops, setStops] = useState([]);

  const [form, setForm] = useState({
    route_id: '',
    route_name: '',
    start_location: '',
    end_location: '',
    estimated_time: '',
    school_id: userObj.school_id || '',
    status: 'A',
    created_by: userObj.user_name || '',
    updated_by: userObj.user_name || ''
  });

  // ---------- Helpers ----------
  // Remove the duplicate toHHMM function from here since it's now defined above

  const allowedKey = (e) => {
    const k = e.key;
    const ctrlCmd = e.ctrlKey || e.metaKey;
    if (/^[A-Za-z0-9]$/.test(k)) return true;
    if (k === ' ' && e.currentTarget.selectionStart > 0) return true;
    const nav = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Escape',
      'Enter',
    ];
    if (nav.includes(k)) return true;
    if (ctrlCmd && ['a', 'c', 'v', 'x', 'z', 'y'].includes(k.toLowerCase())) return true;
    return false;
  };

  const handleRouteNameKeyDown = (e) => {
    if (!allowedKey(e)) e.preventDefault();
    if (e.key === ' ' && e.currentTarget.selectionStart === 0) e.preventDefault();
  };

  const handleRouteNamePaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData('text');
    if (!/^[A-Za-z0-9 ]*$/.test(paste)) {
      e.preventDefault();
      toast.error('Only letters, numbers and spaces are allowed in Route Name.');
    }
  };

  const parseStopTime = (v) => {
    if (!v) return null;
    const parsed = dayjs(v, ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm', 'HH:mm:ss'], true);
    return parsed.isValid() ? parsed : null;
  };

  const formatTimeForAPI = (timeValue) => {
    if (!timeValue) return null;
    if (dayjs.isDayjs(timeValue)) {
      return timeValue.format('HH:mm');
    }
    return toHHMM(timeValue);
  };

  // Stop management functions
  const addStop = () => {
    const newStopSequence = stops.length > 0 
      ? Math.max(...stops.map(stop => parseInt(stop.stop_sequence) || 0)) + 1 
      : 1;
    
    setStops(prev => [...prev, {
      stop_id: null,
      stop_name: '',
      stop_sequence: newStopSequence.toString(),
      pickup_time: null,
      drop_time: null,
      address: '',
      latitude: '',
      longitude: '',
      status: 'A'
    }]);
  };

  const removeStop = (index) => {
    setStops(prev => prev.filter((_, i) => i !== index));
  };

  const updateStop = (index, field, value) => {
    setStops(prev => prev.map((stop, i) => 
      i === index ? { ...stop, [field]: value } : stop
    ));
  };

  const handleStopInputChange = (index, e) => {
    const { id, value } = e.target;
    updateStop(index, id, value);
  };

  const handleStopLatChange = (index, e) => {
    const raw = _coordSanitize(e.target.value);
    updateStop(index, 'latitude', raw);
  };

  const handleStopLngChange = (index, e) => {
    const raw = _coordSanitize(e.target.value);
    updateStop(index, 'longitude', raw);
  };

  const handleStopLatBlur = (index) => {
    const stop = stops[index];
    if (stop.latitude === '') return;
    const n = Number(stop.latitude);
    if (Number.isNaN(n)) return;
    const clamped = _clamp(n, -90, 90);
    updateStop(index, 'latitude', _fmt6(clamped));
  };

  const handleStopLngBlur = (index) => {
    const stop = stops[index];
    if (stop.longitude === '') return;
    const n = Number(stop.longitude);
    if (Number.isNaN(n)) return;
    const clamped = _clamp(n, -180, 180);
    updateStop(index, 'longitude', _fmt6(clamped));
  };

  const validateBeforeSubmit = () => {
    const name = (form.route_name || '').trim();

    if (!name) {
      toast.error('Route Name is required');
      return false;
    }
    if (!/^[A-Za-z0-9 ]+$/.test(name)) {
      toast.error('Route Name should contain only letters, numbers and spaces.');
      return false;
    }
    if (!form.start_location || !form.end_location) {
      toast.error('Start and End Location are required');
      return false;
    }
    if (String(form.start_location) === String(form.end_location)) {
      toast.error('Start and End Location cannot be the same.');
      return false;
    }
    if (!form.estimated_time?.trim()) {
      toast.error('Estimated Time is required');
      return false;
    }
    const hhmm = toHHMM(form.estimated_time);
    if (!hhmm) {
      toast.error('Please enter a valid time in HH:mm format (00-23 hours, 00-59 minutes).');
      return false;
    }

    // Validate stops
    if (stops.length > 0) {
      const sequences = new Set();
      
      for (let i = 0; i < stops.length; i++) {
        const stop = stops[i];
        
        // Validate stop name
        const stopName = (stop.stop_name || '').trim();
        if (!stopName) {
          toast.error(`Stop Name is required for stop ${i + 1}`);
          return false;
        }
        
        if (!isAlnumSpace(stopName)) {
          toast.error(`Stop Name must contain only letters, numbers and spaces for stop ${i + 1}`);
          return false;
        }
        
        // Validate stop sequence
        if (!stop.stop_sequence) {
          toast.error(`Stop Sequence is required for stop ${i + 1}`);
          return false;
        }
        
        if (sequences.has(stop.stop_sequence)) {
          toast.error(`Duplicate Stop Sequence found: ${stop.stop_sequence}`);
          return false;
        }
        sequences.add(stop.stop_sequence);
        
        // Validate coordinates
        if (stop.latitude !== '') {
          const n = Number(stop.latitude);
          if (!Number.isFinite(n) || n < -90 || n > 90) {
            toast.error(`Latitude must be between -90 and 90 for stop ${i + 1}`);
            return false;
          }
        }
        if (stop.longitude !== '') {
          const n = Number(stop.longitude);
          if (!Number.isFinite(n) || n < -180 || n > 180) {
            toast.error(`Longitude must be between -180 and 180 for stop ${i + 1}`);
            return false;
          }
        }
        
        // Validate times
        if (!stop.pickup_time) {
          toast.error(`Pickup Time is required for stop ${i + 1}`);
          return false;
        }
        
        if (!stop.drop_time) {
          toast.error(`Drop Time is required for stop ${i + 1}`);
          return false;
        }
        
        const pMin = toMinutes(stop.pickup_time);
        const dMin = toMinutes(stop.drop_time);
        
        if (pMin == null || dMin == null) {
          toast.error(`Pickup and Drop Time are required for stop ${i + 1}`);
          return false;
        }
        if (pMin === dMin) {
          toast.error(`Pickup and drop times cannot be the same for stop ${i + 1}`);
          return false;
        }
        if (pMin > dMin) {
          toast.error(`Pickup time cannot be later than drop time for stop ${i + 1}`);
          return false;
        }
      }
    }

    return true;
  };

  useEffect(() => {
    startlocationData();
  }, []);

  const startlocationData = async () => {
    try {
      const response = await axios.post(baseUrl + '/routelocation/', {
        action: 'READ',
        school_id: userObj.school_id,
      });
      setRoutelocations(response.data || []);
    } catch (error) {
      toast.error('Failed to load locations');
    }
  };

 useEffect(() => {
  if (routeLocation.state?.routesData && routelocations.length) {
    const routesData = routeLocation.state.routesData;

    const resolveLocationId = (idVal, nameVal) => {
      // Prefer ID if present
      if (idVal !== null && idVal !== undefined && idVal !== "") {
        const byId = routelocations.find(
          (r) => String(r.route_location_id) === String(idVal)
        );
        if (byId) return String(byId.route_location_id);
      }

      // Fallback: match by name (start_location_name / end_location_name)
      if (nameVal) {
        const byName = routelocations.find(
          (r) => String(r.location_name).trim() === String(nameVal).trim()
        );
        if (byName) return String(byName.route_location_id);
      }

      return "";
    };

    const startId = resolveLocationId(
      routesData.start_location,        // likely ID
      routesData.start_location_name    // name
    );

    const endId = resolveLocationId(
      routesData.end_location,          // likely ID
      routesData.end_location_name      // name
    );

    setForm({
      route_id: routesData.route_id || "",
      route_name: routesData.route_name || "",
      start_location: startId,   // ✅ must be ID
      end_location: endId,       // ✅ must be ID
      estimated_time: routesData.estimated_time || "",
      school_id: routesData.school_id || userObj.school_id,
      status: routesData.status || "A",
      created_by: userObj.user_name || "",
      updated_by: userObj.user_name || "",
    });

    if (routesData.route_id) {
      setEditId(routesData.route_id);

      if (routesData.stops && Array.isArray(routesData.stops)) {
        const formattedStops = routesData.stops.map((stop) => ({
          stop_id: stop.stop_id || null,
          stop_name: stop.stop_name || "",
          stop_sequence: stop.stop_sequence?.toString() || "",
          pickup_time: parseStopTime(stop.pickup_time),
          drop_time: parseStopTime(stop.drop_time),
          address: stop.address || "",
          latitude: stop.latitude != null ? String(stop.latitude) : "",
          longitude: stop.longitude != null ? String(stop.longitude) : "",
          status: stop.status || "A",
        }));
        setStops(formattedStops);
      }
    }

    // optional: clear state so back/refresh doesn't re-trigger
    // window.history.replaceState({}, document.title);
  }
}, [routelocations, routeLocation.state, userObj.school_id, userObj.user_name]);


  const endLocationOptions = useMemo(() => {
    return (routelocations || []).filter((r) => r.status === 'Active');
  }, [routelocations]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit()) return;

    const hhmm = toHHMM(form.estimated_time);
    const action = editId ? 'UPDATE' : 'CREATE';

    // Prepare stops data for API
    const stopsPayload = stops.map(stop => ({
      stop_id: stop.stop_id,
      stop_name: stop.stop_name.trim(),
      stop_sequence: parseInt(stop.stop_sequence),
      latitude: stop.latitude ? parseFloat(stop.latitude) : null,
      longitude: stop.longitude ? parseFloat(stop.longitude) : null,
      address: stop.address || null,
      pickup_time: formatTimeForAPI(stop.pickup_time),
      drop_time: formatTimeForAPI(stop.drop_time),
      status: stop.status || 'A'
    }));

    const payload = {
      action: action,
      route_id: editId || null,
      route_name: form.route_name.trim(),
      school_id: parseInt(userObj.school_id),
      start_location: parseInt(form.start_location),
      end_location: parseInt(form.end_location),
      estimated_time: hhmm,
      status: form.status,
      stops: stopsPayload.length > 0 ? stopsPayload : null,
      created_by: userObj.user_name,
      updated_by: userObj.user_name
    };

    try {
      setIsLoading(true);
      await axios.post(baseUrl + '/routes', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      
      toast.success(editId ? 'Route updated successfully' : 'Route added successfully');
      
      // Reset form
      setForm({
        route_id: '',
        route_name: '',
        start_location: '',
        end_location: '',
        estimated_time: '',
        school_id: userObj.school_id,
        status: 'A',
        created_by: userObj.user_name,
        updated_by: userObj.user_name
      });
      setStops([]);
      setEditId(null);
      
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const msg = data?.error || data?.message || 'Unexpected error occurred';
      
      if (status === 409) {
        toast.error('Route with this name already exists');
      } else if (status === 404) {
        toast.error('Route not found');
      } else if (status === 400) {
        toast.error(msg || 'Invalid request data');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editId) {
      toast.error('No route selected for deletion');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this route? This will also delete all associated stops.')) {
      return;
    }

    const payload = {
      action: 'DELETE',
      route_id: editId,
      school_id: parseInt(userObj.school_id),
      updated_by: userObj.user_name
    };

    try {
      setIsLoading(true);
      await axios.post(baseUrl + '/routes', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      
      toast.success('Route deleted successfully');
      
      // Reset and navigate back
      setForm({
        route_id: '',
        route_name: '',
        start_location: '',
        end_location: '',
        estimated_time: '',
        school_id: userObj.school_id,
        status: 'A',
        created_by: userObj.user_name,
        updated_by: userObj.user_name
      });
      setStops([]);
      setEditId(null);
      
      setTimeout(() => {
        navigate('/bus-routes');
      }, 1000);
      
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const msg = data?.error || data?.message || 'Unexpected error occurred';
      
      if (status === 404) {
        toast.error('Route not found');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
                      <div style={{
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        paddingRight: '10px'
                      }}>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">Route Details</h6>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Route Name <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="text"
                        id="route_name"
                        value={form.route_name}
                        placeholder="Enter Route Name"
                        maxLength={20}
                        onChange={handleInputChange}
                        onKeyDown={handleRouteNameKeyDown}
                        onPaste={handleRouteNamePaste}
                        pattern="[A-Za-z0-9 ]+"
                        title="Use letters, numbers and spaces only"
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Start Location <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Select
                        required
                        id="start_location"
                        value={form.start_location}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled hidden>Select Start Location</option>
                        {(routelocations || [])
                          .filter((r) => r.status === 'Active')
                          .map((r) => (
                            <option key={r.route_location_id} value={String(r.route_location_id)}>
                              {r.location_name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        End Location <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Select
                        required
                        id="end_location"
                        value={form.end_location}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled hidden>Select End Location</option>
                        {endLocationOptions.map((r) => (
                          <option key={r.route_location_id} value={String(r.route_location_id)}>
                            {r.location_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Estimated Time <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="text"
                        inputMode="numeric"
                        id="estimated_time"
                        value={form.estimated_time}
                        placeholder="HH:mm"
                        maxLength={5}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                          const formatted =
                            digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
                          setForm((p) => ({ ...p, estimated_time: formatted }));
                        }}
                        onBlur={(e) => {
                          const v = toHHMM(e.target.value);
                          setForm((p) => ({ ...p, estimated_time: v ?? '' }));
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              {/* Stop Details Section */}
              <Row className="mt-4">
                <Col xs={12}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="commonSectionTitle mb-0">Stop Details</h6>
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-info"
                      onClick={addStop}
                    >
                      + Add Stop
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* Stop Details - Dynamically Rendered */}
              {stops.map((stop, index) => (
                <div key={index} className="stop-section mt-3 p-3 border rounded">
                  <Row>
                    <Col xs={12}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Stop {index + 1}</h6>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          onClick={() => removeStop(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Stop Name <span className="requiredStar">*</span></Form.Label>
                          <Form.Control
                            required
                            type="text"
                            id="stop_name"
                            value={stop.stop_name}
                            placeholder="Enter Stop Name"
                            maxLength={40}
                            onChange={(e) => handleStopInputChange(index, e)}
                            onKeyDown={handleStopNameKeyDown}
                            onPaste={handleStopNamePaste}
                            pattern="[A-Za-z0-9 ]+"
                            title="Use letters, numbers and spaces only"
                            onKeyUp={(e) => {
                              const v = e.currentTarget.value.replace(/\s{2,}/g, ' ');
                              if (v !== stop.stop_name) updateStop(index, 'stop_name', v);
                            }}
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Stop Sequence <span className="requiredStar">*</span></Form.Label>
                          <Form.Control
                            required
                            type="text"
                            id="stop_sequence"
                            value={stop.stop_sequence}
                            maxLength={2}
                            placeholder="Enter Stop Sequence"
                            inputMode="numeric"
                            onChange={(e) => {
                              const filtered = e.target.value.replace(/\D/g, '');
                              updateStop(index, 'stop_sequence', filtered);
                            }}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Pickup Time <span className="requiredStar">*</span></Form.Label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              ampm
                              minutesStep={5}
                              value={dayjs.isDayjs(stop.pickup_time) ? stop.pickup_time : null}
                              onChange={(val) => updateStop(index, 'pickup_time', val)}
                              renderInput={(params) => <TextField {...params} required fullWidth size="small" />}
                            />
                          </LocalizationProvider>
                        </Form.Group>
                      </div>
                    </Col>

                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Drop Time <span className="requiredStar">*</span></Form.Label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              ampm
                              minutesStep={5}
                              value={dayjs.isDayjs(stop.drop_time) ? stop.drop_time : null}
                              onChange={(val) => updateStop(index, 'drop_time', val)}
                              renderInput={(params) => <TextField {...params} required fullWidth size="small" />}
                            />
                          </LocalizationProvider>
                        </Form.Group>
                      </div>
                    </Col>

                    <Col xs={12} md={12} lg={8} xxl={6}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            type="text"
                            id="address"
                            value={stop.address}
                            placeholder="Enter Address"
                            onChange={(e) => handleStopInputChange(index, e)}
                            maxLength={250}
                            rows="2"
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Latitude</Form.Label>
                          <Form.Control
                            type="text"
                            id="latitude"
                            value={stop.latitude}
                            placeholder="Enter Coordinates"
                            inputMode="decimal"
                            onChange={(e) => handleStopLatChange(index, e)}
                            onBlur={() => handleStopLatBlur(index)}
                          />
                        </Form.Group>
                      </div>
                    </Col>

                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Longitude</Form.Label>
                          <Form.Control
                            type="text"
                            id="longitude"
                            value={stop.longitude}
                            placeholder="Enter Coordinates"
                            inputMode="decimal"
                            onChange={(e) => handleStopLngChange(index, e)}
                            onBlur={() => handleStopLngBlur(index)}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}

              <div className="d-flex justify-content-between mt-4">
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-info clearBtn"
                    onClick={() => {
                      setForm({
                        route_id: '',
                        route_name: '',
                        start_location: '',
                        end_location: '',
                        estimated_time: '',
                        school_id: userObj.school_id,
                        status: 'A',
                        created_by: userObj.user_name,
                        updated_by: userObj.user_name
                      });
                      setStops([]);
                      setEditId(null);
                    }}
                    disabled={isLoading}
                  >
                    Reset
                  </Button>
                </div>
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-danger secondaryBtn me-2"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="btn-success primaryBtn"
                    // disabled={isLoading}
                  >Submit
                    {/* {isLoading ? 'Processing...' : (editId ? 'Update' : 'Submit')} */}
                  </Button>
                </div>
              </div>
            </form>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AddBusRoutes;